import React from 'react';
import type { CustomSection as CustomSectionType } from '../lib/frontend-db';

interface Props {
  sections: CustomSectionType[];
}

const CustomSection: React.FC<Props> = ({ sections }) => {
  if (!sections || sections.length === 0) {
    return null;
  }

  return (
    <>
      {sections.map((section) => {
        const {
          id,
          title,
          content,
          image_url,
          button_text,
          button_url,
          settings = {}
        } = section;

        const {
          background_color = 'transparent',
          text_color = 'inherit',
          button_style = 'primary',
          image_position = 'top',
          padding = 'py-16'
        } = settings;
        
        // Adjust for dark mode
        const bgColor = background_color === 'transparent' 
          ? 'var(--bg-secondary)' 
          : background_color;
        const textColorFinal = text_color === 'inherit' 
          ? 'var(--text-primary)' 
          : text_color;

        const buttonClasses = {
          primary: 'bg-[var(--primary-color)] text-white',
          secondary: 'bg-white text-[var(--primary-color)] border border-[var(--primary-color)]',
          text: 'text-[var(--primary-color)] underline'
        }[button_style] || 'bg-[var(--primary-color)] text-white';

        // Render content section
        const renderContent = () => (
          <div className="space-y-4 flex-1">
            <h2 className="text-3xl font-bold text-center text-[var(--primary-color)]">
              {title}
            </h2>
            <div className="prose max-w-none mx-auto text-center" style={{ color: textColorFinal }}>
              {content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-4">{paragraph}</p>
              ))}
            </div>
            {button_text && button_url && (
              <div className="text-center mt-8">
                <a
                  href={button_url}
                  className={`inline-block px-6 py-2 rounded-md transition-opacity hover:opacity-90 ${buttonClasses}`}
                >
                  {button_text}
                </a>
              </div>
            )}
          </div>
        );

        // Render image section
        const renderImage = () => (
          image_url && (
            <div className={`${image_position === 'top' ? 'w-full' : 'flex-1'}`}>
              <img
                src={image_url}
                alt={title}
                className={`w-full object-cover rounded-lg shadow-md ${
                  image_position === 'top' ? 'h-64' : 'h-full min-h-64'
                }`}
              />
            </div>
          )
        );

        return (
          <section key={id} id={`custom-section-${id}`} className={`${padding} transition-colors`} style={{ backgroundColor: bgColor }}>
            <div className="container mx-auto px-4">
              {image_position === 'top' && (
                <div className="space-y-8">
                  {renderImage()}
                  {renderContent()}
                </div>
              )}

              {image_position === 'left' && (
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  <div className="lg:w-1/2">
                    {renderImage()}
                  </div>
                  <div className="lg:w-1/2">
                    {renderContent()}
                  </div>
                </div>
              )}

              {image_position === 'right' && (
                <div className="flex flex-col lg:flex-row-reverse gap-8 items-center">
                  <div className="lg:w-1/2">
                    {renderImage()}
                  </div>
                  <div className="lg:w-1/2">
                    {renderContent()}
                  </div>
                </div>
              )}

              {/* Fallback for when no image is present */}
              {!image_url && renderContent()}
            </div>
          </section>
        );
      })}
    </>
  );
};

export default CustomSection;

